import {
  List,
  TextField,
  TextInput,
  DatagridConfigurable,
  ReferenceField,
  ExportButton,
  ReferenceInput,
  FunctionField,
  SelectInput,
  AutocompleteInput,
  TopToolbar,
  FilterButton,
  SelectColumnsButton,
  WrapperField,
  useListContext,
  useDataProvider,
} from "react-admin";
import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { FaBarcode, FaEye } from "react-icons/fa";
import {
  HiOutlineCalculator,
  HiOutlineCash,
  HiOutlineCreditCard,
  HiOutlineFilter,
  HiOutlineMinusCircle,
  HiOutlineQuestionMarkCircle,
  HiOutlineSwitchHorizontal,
  HiOutlineUserRemove,
  HiOutlineX,
  HiReceiptTax,
} from "react-icons/hi";
import { PixIcon } from "../../components/PaymentMethodChip";
import { IuguIcon } from "../../components/IuguIcon";
import { AppDateInput } from "../../components/AppDateInput";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { PaymentMethodChip } from "../../components/PaymentMethodChip";
// Cor por família de categoria: compartilhada com a ficha da transação
import { transactionCategoryColor as categoryColor } from "./transactions-util";
import { ListPageIndicator } from "../../components/TablePageIndicator";
import { RaRowActions } from "../../components/RaRowActions";
import { AppBulkDeleteButton } from "../../components/AppBulkDeleteButton";
import { DateOnlyField } from "../../components/DateOnlyField";
import { determineUserReadableRole } from "../users/UserUtils";
import { colors, fontFamilyMono, radius, shadows } from "../../data/tokens";


// Estilo compacto de tabela — espelho do datagridSx da lista de Sócios
const datagridSx = {
  "& .RaDatagrid-headerCell": {
    fontSize: 11.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: colors.textMuted,
    backgroundColor: colors.bgSubtle,
    paddingTop: 1,
    paddingBottom: 1,
  },
  "& .RaDatagrid-rowCell": { paddingTop: 0.75, paddingBottom: 0.75 },
};

const filterToQueryMember = (searchText: any) => ({
  mailName: `${searchText}`,
});

// Formas de pagamento reais da base (levantamento de 04/08/2026, 28
// valores distintos), curadas em opções "guarda-chuva": o filtro do
// servidor é "contém", então "Boleto" alcança todas as variantes
// gravadas ("Boleto Bancário", "(IUGU)", digitações antigas) e "Isen"
// alcança Isenção/Isento em todas as formas. As ~9,4 mil transações
// SEM forma registrada não são filtráveis por aqui (precisaria de
// apoio do servidor para "campo vazio").
const paymentMethodChoices = [
  { id: "Boleto", name: "Boleto Bancário (todas as variantes)" },
  // Só os emitidos pela Iugu. O boleto COMUM sozinho não é filtrável
  // (precisaria de "não contém" no servidor) — por isso não há opção
  { id: "Boleto Bancário (IUGU)", name: "Boleto Bancário (só Iugu)" },
  { id: "Isen", name: "Isenções / Isentos (todas)" },
  { id: "Pix", name: "Pix" },
  { id: "Transferência Eletrônica", name: "Transferência Eletrônica" },
  { id: "Cartão de Crédito", name: "Cartão de Crédito" },
  { id: "Cartão de Débito", name: "Cartão de Débito" },
  { id: "Dinheiro", name: "Dinheiro / Depósito" },
  { id: "Cheque", name: "Cheque (à vista e pré-datado)" },
  { id: "Reativação de Matrícula", name: "Reativação de Matrícula" },
];

// Sugestão só com o nome (decisão do cliente). Digitar CPF ou e-mail
// na busca continua funcionando — o filtro mailName cobre os três;
// sem nome cadastrado, cai para o e-mail para a opção não ficar vazia.
const optionRenderer = (choice: any) =>
  String(choice?.fullName || choice?.email || choice?.id || "");

// ------------------------------------------------------------------
// Faixa de KPIs (fase 1 — só CONTAGENS, que a API já fornece de graça
// no total de cada listagem filtrada; somas em R$ dependem de endpoint
// de agregação no backend — TODO item 10). Os recortes reagem ao
// filtro ativo: cada contagem re-consulta com o filtro atual + recorte.
// ------------------------------------------------------------------
const kpiBoxSx = {
  backgroundColor: colors.bgSubtle,
  border: `1px solid ${colors.border}`,
  borderRadius: `${radius.md}px`,
  padding: "8px 14px",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
};
const kpiLabelSx = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.04em",
  color: colors.textMuted,
};
const kpiValueSx = {
  fontSize: 18,
  fontWeight: 700,
  color: colors.primaryDark,
  lineHeight: 1.3,
};

const TransactionsKpis = () => {
  const { total, filterValues } = useListContext();
  const dataProvider = useDataProvider();
  const [counts, setCounts] = useState<any | null>(null);
  // Somas em R$ do recorte atual (fase 2 dos KPIs — endpoint de
  // agregação read-only que honra os mesmos filtros da listagem)
  const [agg, setAgg] = useState<any | null>(null);
  const filterKey = JSON.stringify(filterValues ?? {});
  useEffect(() => {
    let alive = true;
    setCounts(null);
    setAgg(null);
    const auth = localStorage.getItem("auth");
    const token = auth ? JSON.parse(auth)?.access_token : "";
    // O trio filter+range+sort vai completo: o grabRequestFilters do
    // servidor só considera o filtro quando range está presente
    // (dialeto do react-admin) — só o filter seria descartado e a
    // agregação voltaria os números GLOBAIS em silêncio
    const aggParams = new URLSearchParams({
      filter: JSON.stringify(filterValues ?? {}),
      range: "[0,0]",
      sort: '["id","ASC"]',
    });
    fetch(
      `${process.env.REACT_APP_API_PATH}/transactions/aggregate?${aggParams}`,
      { headers: { Authorization: "Bearer " + token } }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((a) => {
        if (alive) setAgg(a);
      })
      .catch(() => {
        if (alive) setAgg(null);
      });
    const countWith = (extra: any) =>
      dataProvider
        .getList("transactions/crud", {
          pagination: { page: 1, perPage: 1 },
          sort: { field: "id", order: "ASC" },
          filter: { ...(filterValues ?? {}), ...extra },
        })
        .then((r: any) => r.total ?? 0)
        .catch(() => null);
    Promise.all([
      countWith({ paymentMethod: "Boleto" }),
      // Boletos emitidos pela Iugu: termo completo (contém), pega só a
      // variante "Boleto Bancário (IUGU)". O boleto comum não tem como
      // ser filtrado direto (o servidor não tem "não contém") — sai por
      // SUBTRAÇÃO na montagem dos cards: total de boletos − Iugu.
      countWith({ paymentMethod: "Boleto Bancário (IUGU)" }),
      countWith({ paymentMethod: "Pix" }),
      // Termo completo de propósito: o filtro é "contém" e "Cartão"
      // sozinho somaria o Cartão de Débito
      countWith({ paymentMethod: "Cartão de Crédito" }),
      countWith({ paymentMethod: "Transferência Eletrônica" }),
      countWith({ paymentMethod: "Isen" }),
      countWith({ hasMemberId: "false" }),
      // 2º maior grupo da base (1/3): dado herdado sem forma registrada
      countWith({ hasPaymentMethod: "false" }),
    ]).then(
      ([
        boleto,
        boletoIugu,
        pix,
        credito,
        transferencia,
        isencao,
        orfas,
        semForma,
      ]) => {
        if (alive)
          setCounts({
            boleto,
            boletoIugu,
            pix,
            credito,
            transferencia,
            isencao,
            orfas,
            semForma,
          });
      }
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const fmt = (v: number | null | undefined) =>
    v === null || v === undefined ? "…" : v.toLocaleString("pt-BR");
  const brl = (v: number | null | undefined) =>
    v === null || v === undefined
      ? "…"
      : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  // 12 cards em 2 linhas de 6 (grid): linha 1 = recorte, valores em R$ e
  // pontos de atenção; linha 2 = formas de pagamento
  const items: {
    label: string;
    value: number | null;
    icon: JSX.Element;
    iconColor?: string;
    warn?: boolean;
    money?: boolean;
    title?: string;
  }[] = [
    {
      label: "No filtro",
      value: total ?? null,
      icon: <HiOutlineFilter size={18} />,
    },
    {
      label: "Valor no filtro",
      value: agg?.sum ?? null,
      money: true,
      icon: <HiOutlineCash size={18} />,
      iconColor: colors.success,
      title: "Soma dos valores das transações do recorte atual",
    },
    {
      // Média SEM os R$ 0,00 — as isenções zeradas derrubariam o número
      label: "Ticket médio",
      value: agg?.nonZero?.avg ?? null,
      money: true,
      icon: <HiOutlineCalculator size={18} />,
      title:
        agg?.zeroCount != null
          ? `Média excluindo transações de R$ 0,00 (${fmt(
              agg.zeroCount
            )} zeradas no recorte)`
          : "Média excluindo transações de R$ 0,00",
    },
    {
      label: "Zeradas (R$ 0,00)",
      value: agg?.zeroCount ?? null,
      icon: <HiOutlineMinusCircle size={18} />,
      title:
        "Transações com valor R$ 0,00 no recorte (isenções e afins) — fora do ticket médio",
    },
    {
      label: "Órfãs (sem sócio)",
      value: counts?.orfas ?? null,
      icon: <HiOutlineUserRemove size={18} />,
      iconColor: colors.warning,
      warn: true,
    },
    {
      // Dívida de dado herdada: acompanha a arrumação conforme as formas
      // forem sendo registradas (clicar no filtro "Forma registrada? Não"
      // lista exatamente estas)
      label: "Sem forma registrada",
      value: counts?.semForma ?? null,
      icon: <HiOutlineQuestionMarkCircle size={18} />,
      iconColor: colors.warning,
      warn: true,
    },
    {
      // Boleto comum por subtração (total − Iugu); só mostra número
      // quando as duas contagens chegaram
      label: "Boleto",
      value:
        counts?.boleto != null && counts?.boletoIugu != null
          ? counts.boleto - counts.boletoIugu
          : null,
      icon: <FaBarcode size={16} />,
    },
    {
      label: "Boleto Iugu",
      value: counts?.boletoIugu ?? null,
      icon: <IuguIcon size={18} />,
    },
    {
      label: "Pix",
      value: counts?.pix ?? null,
      icon: <PixIcon size={16} />,
      iconColor: colors.pix,
    },
    {
      label: "Cartão de Crédito",
      value: counts?.credito ?? null,
      icon: <HiOutlineCreditCard size={18} />,
    },
    {
      label: "Transferência",
      value: counts?.transferencia ?? null,
      icon: <HiOutlineSwitchHorizontal size={18} />,
    },
    {
      label: "Isenções",
      value: counts?.isencao ?? null,
      icon: <HiReceiptTax size={18} />,
    },
  ];
  return (
    <Box
      sx={{
        // Grid responsivo: 6 colunas no desktop (2 linhas certinhas de 6
        // cards), degradando para 4/3/2 conforme a largura
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          sm: "repeat(3, 1fr)",
          md: "repeat(4, 1fr)",
          lg: "repeat(6, 1fr)",
        },
        gap: 1.5,
        padding: "4px 0 10px",
      }}
    >
      {items.map((item) => (
        <Box key={item.label} sx={kpiBoxSx} title={item.title}>
          <Box>
            <Typography sx={kpiLabelSx}>{item.label}</Typography>
            <Typography
              sx={{
                ...kpiValueSx,
                // Valores em R$ um pouco menores para caber no card
                ...(item.money ? { fontSize: 15 } : {}),
                // Órfãs em tom de atenção quando existirem
                ...(item.warn && item.value ? { color: colors.warning } : {}),
              }}
            >
              {item.money ? brl(item.value) : fmt(item.value)}
            </Typography>
          </Box>
          {/* Ícone lateral no mesmo padrão dos cartões do Dashboard */}
          <Box
            sx={{
              backgroundColor: colors.bgContent,
              borderRadius: `${radius.sm}px`,
              padding: 0.75,
              display: "flex",
              color: item.iconColor || colors.primary,
              flexShrink: 0,
            }}
          >
            {item.icon}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// Limpa todos os filtros ativos; só aparece quando existe filtro aplicado
const ClearAllFiltersButton = () => {
  const { filterValues, setFilters } = useListContext();
  if (!filterValues || Object.keys(filterValues).length === 0) return null;
  return (
    <Button
      size="small"
      color="secondary"
      startIcon={<HiOutlineX size={16} />}
      onClick={() => setFilters({}, [])}
    >
      Limpar filtros
    </Button>
  );
};

export const TransactionsList = () => {
  const isAdmin =
    determineUserReadableRole(localStorage.getItem("roles")) === "admin";
  const filterToQueryTransactions = (searchText: any) => ({
    id: `${searchText}`,
  });

  const ListActions = () => (
    <TopToolbar sx={{ alignItems: "center" }}>
      <SelectColumnsButton preferenceKey="transactions.datagrid.v2" />
      <FilterButton />
      <ExportButton />
      <ClearAllFiltersButton />
      {/* navegação ancorada à direita; botões ficam à esquerda */}
      <span style={{ marginLeft: "auto", display: "flex" }}>
        <ListPageIndicator />
      </span>
    </TopToolbar>
  );

  const postFilters = [
    <ReferenceInput
      source="memberId"
      reference="users/crud"
      // Consulta só a partir de 2 caracteres. A checagem antiga olhava
      // "name" — chave que nunca existe no filtro (o filterToQuery gera
      // mailName) — e por isso buscava até com o campo vazio.
      enableGetChoices={({ mailName }) =>
        Boolean(mailName && String(mailName).trim().length >= 2)
      }
      alwaysOn
    >
      <AutocompleteInput
        label="Nome/E-mail/CPF"
        optionText={optionRenderer}
        optionValue="id"
        filterToQuery={filterToQueryMember}
        noOptionsText="Digite nome, e-mail ou CPF para buscar"
        // Largura garantida no próprio componente (vence qualquer regra
        // do formulário de filtros); o popup acompanha o campo
        sx={{ minWidth: 340 }}
        // Sugestão em linha única com reticências quando o nome não cabe
        ListboxProps={{
          sx: {
            "& .MuiAutocomplete-option": {
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          },
        }}
      />
    </ReferenceInput>,
    <AppDateInput source="referenceDate" label="Data Referência" />,
    <ReferenceInput
      reference="orphaned-users/crud"
      source="orphanedMemberId"
      label="ID Membro Órfão"
      perPage={3000}
    >
      <AutocompleteInput
        label="ID Membro Órfão"
        optionText="sNome"
        optionValue="id"
        filterToQuery={filterToQueryTransactions}
        suggestionLimit={50}
        // O root do Autocomplete não estica sozinho — width 100% o faz
        // preencher a célula padrão da grade, como os demais filtros
        sx={{ width: "100%" }}
        // Sugestões em linha única com reticências (nomes longos)
        ListboxProps={{
          sx: {
            "& .MuiAutocomplete-option": {
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          },
        }}
      />
    </ReferenceInput>,
    <TextInput label="Competência" source="sCompetencia" />,
    <SelectInput
      label="Forma de Pgto."
      source="paymentMethod"
      choices={paymentMethodChoices}
      emptyText="Todas"
    />,
    <ReferenceInput
      label="Categoria"
      source="id_Receita"
      reference="transaction-categories/crud"
    >
      <SelectInput label="Categoria" optionText="name" emptyText="Todas" />
    </ReferenceInput>,
    <SelectInput
      label="Transações órfãs"
      source="hasMemberId"
      choices={[
        { id: "true", name: "Não" },
        { id: "false", name: "Sim" },
      ]}
      emptyText="Todas"
    />,
    // TODO item 16: 1/3 da base não tem forma de pagamento registrada
    // (dado herdado) — este filtro torna essas transações localizáveis
    <SelectInput
      label="Forma registrada?"
      source="hasPaymentMethod"
      choices={[
        { id: "true", name: "Sim" },
        { id: "false", name: "Não" },
      ]}
      emptyText="Todas"
    />,
  ];

  return (
    <>
      <PageTopBreadcrumb
        title="Transações"
        firstPath="/#/transactions/crud"
        actionType="create"
      />
      <List
        title=" "
        filters={postFilters}
        actions={<ListActions />}
        perPage={25}
        // Mantém a página atual visível enquanto a próxima carrega
        queryOptions={{ keepPreviousData: true }}
        sx={{
          // Mesmo padrão visual da lista de Sócios: filtros em grade
          // regular, botão ⊗ como badge e tudo dentro do cartão branco.
          // (Antes a lista vivia num Card próprio e os filtros na linha
          // flex padrão do react-admin — com vários ativos, quebrava.)
          "& .RaList-main": {
            backgroundColor: colors.white,
            borderRadius: `${radius.lg}px`,
            boxShadow: shadows.card,
            padding: "12px 20px 8px",
          },
          "& .RaList-content": {
            boxShadow: "none",
            backgroundColor: "transparent",
          },
          // Toolbar em pilha: cluster de ações no topo e o formulário
          // de filtros embaixo em linhas limpas
          "& .RaList-actions": {
            display: "flex",
            flexDirection: "column-reverse",
            alignItems: "stretch",
            gap: 1,
          },
          "& .RaList-actions .MuiToolbar-root": {
            position: "static",
            gap: 1,
            width: "100%",
            justifyContent: "flex-start",
            minHeight: "auto !important",
            paddingBottom: 0,
          },
          "& .RaList-actions > span:empty": { display: "none" },
          // Filtros em linha única elástica: cada filtro cresce/encolhe
          // para caber na largura; campos e botões ⊗ centralizados
          "& .RaList-actions form": {
            width: "100%",
            alignItems: "center",
            rowGap: 1.5,
            columnGap: 1.5,
            paddingBottom: 1,
            paddingTop: 0,
            minHeight: "auto",
          },
          "& .RaFilterForm-filterFormInput": {
            display: "flex",
            alignItems: "center",
            position: "relative",
            marginTop: "0 !important",
            marginBottom: "0 !important",
            // Linha única elástica: os campos crescem e encolhem
            // (base 170, piso 150, teto 320) para caber todos os
            // filtros juntos na mesma linha, ajustando à largura
            flex: "1 1 170px",
            minWidth: 150,
            maxWidth: 320,
          },
          "& .RaFilterForm-filterFormInput .MuiFormControl-root": {
            width: "100%",
            marginTop: "0 !important",
            marginBottom: "0 !important",
          },
          // A busca principal é mais larga que a grade de 240px, sem
          // ocupar a linha toda — o wrapper do filtro carrega
          // data-source; a largura mínima real está no sx do próprio
          // AutocompleteInput (340px), que nenhuma regra externa atropela
          "& .RaFilterForm-filterFormInput[data-source='memberId']": {
            flex: "0 1 380px",
            minWidth: 340,
          },
          // Botão ⊗ como badge no canto superior direito do campo
          "& .RaFilterFormInput-hideButton": {
            position: "absolute",
            top: -10,
            right: -10,
            zIndex: 1,
            padding: 0,
            marginTop: "0 !important",
            marginBottom: "0 !important",
            color: colors.textMuted,
            backgroundColor: colors.white,
            borderRadius: "50%",
            "& svg": { fontSize: 22 },
            "&:hover": {
              color: colors.action,
              backgroundColor: colors.white,
            },
          },
          "& .RaFilterFormInput-spacer": { display: "none" },
        }}
      >
        <TransactionsKpis />
        {/* preferenceKey versionada (v2): a lista de colunas fica
            persistida nas preferências do navegador e só se renova
            quando o NÚMERO de colunas muda — o menu "⋮" antigo, sem
            rótulo, deixou "Unlabeled column #6" gravado em quem já
            tinha aberto o seletor. Chave nova = cache limpo p/ todos. */}
        <DatagridConfigurable
          sx={datagridSx}
          preferenceKey="transactions.datagrid.v2"
          bulkActionButtons={<AppBulkDeleteButton />}
        >
          <FunctionField
            label="Nome/Membro"
            render={(record: any) =>
              record.memberId ? (
                <ReferenceField
                  source="memberId"
                  reference="users/crud"
                  link="show"
                >
                  {/* Nome como texto-link (navy), não chip: chip aqui
                      competia com o de categoria e tudo parecia igual */}
                  <FunctionField
                    render={(member: any) => (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: colors.primaryDark,
                        }}
                      >
                        {member?.fullName}
                      </Typography>
                    )}
                  />
                </ReferenceField>
              ) : record.orphanedMemberId ? (
                <Button
                  variant="outlined"
                  title={"Membro órfão: " + record.orphanedMemberId}
                  size="small"
                  startIcon={<FaEye />}
                  onClick={() => {
                    window.location.href = `/#/orphaned-users/crud/${record.orphanedMemberId}/show`;
                  }}
                >
                  M. Órfão
                </Button>
              ) : (
                <Typography
                  component="span"
                  sx={{ fontSize: 13, color: colors.textMuted }}
                >
                  —
                </Typography>
              )
            }
          />
          <ReferenceField
            source="id_Receita"
            reference="transaction-categories/crud"
            label="Categoria"
            link="show"
          >
            <FunctionField
              render={(cat: any) =>
                cat?.name ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={categoryColor(cat.name)}
                    label={cat.name}
                  />
                ) : null
              }
            />
          </ReferenceField>

          <DateOnlyField
            label="Data Referência"
            source="referenceDate"
            fontSize={13}
          />
          <FunctionField
            label="Valor"
            render={(record: any) => (
              <Typography
                component="span"
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: fontFamilyMono,
                  whiteSpace: "nowrap",
                  // Verde para receita, vermelho para despesa (tokens)
                  color:
                    record.type === "EXPENSE" ? colors.action : colors.success,
                }}
              >
                {Number(record.value ?? 0).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </Typography>
            )}
          />

          {/* Badge global com ícone e cor por família (sortBy preserva a
              ordenação que o TextField tinha) */}
          <FunctionField
            label="Forma de pagamento"
            sortBy="paymentMethod"
            render={(record: any) => (
              <PaymentMethodChip method={record.paymentMethod} />
            )}
          />
          <TextField
            label="Competência"
            source="sCompetencia"
            sx={{ fontSize: 13, fontFamily: fontFamilyMono }}
          />
          {/* Ações visíveis (Ver sempre; Editar/Excluir só admin) no
              lugar do menu ⋮ que escondia tudo atrás de um clique */}
          <WrapperField label="Ações" textAlign="right">
            <RaRowActions show edit={isAdmin} del={isAdmin} />
          </WrapperField>
        </DatagridConfigurable>
      </List>
    </>
  );
};
